import axios from 'axios';
import type { BaseApiResponse } from '@/interfaces/service/api.interface';
import type { Token } from '@/interfaces/service/auth.interface';
import { getApiResponsePayload } from '@/utils/api.util';
import { TOKEN_REFRESH_URL } from '@/libs/constants/url';
import { getToken, setToken } from '@/libs/utils/authn';

type TokenRefreshEnvelope = Partial<BaseApiResponse<Token>> & Partial<Token>;

const tokenRefreshClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BASE_URL,
  timeout: 10000
});

const getRefreshedToken = (response: TokenRefreshEnvelope): Token | undefined => {
  const envelopedToken = getApiResponsePayload(response);
  if (envelopedToken?.accessToken && envelopedToken.refreshToken) return envelopedToken;

  if (response.accessToken && response.refreshToken) {
    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken
    };
  }

  return undefined;
};

export const refreshAccessToken = async (): Promise<string> => {
  const currentToken = getToken();
  if (!currentToken?.refreshToken) throw new Error('No refresh token');

  const { data: response } = await tokenRefreshClient.post<TokenRefreshEnvelope>(TOKEN_REFRESH_URL, null, {
    headers: {
      RefreshToken: currentToken.refreshToken
    }
  });

  const responseStatus = Number(response.status);
  if (response.status !== undefined && (!Number.isFinite(responseStatus) || responseStatus < 200 || responseStatus >= 400)) {
    throw new Error(response.errorMessage ?? response.message ?? 'Failed to refresh token');
  }

  const refreshedToken = getRefreshedToken(response);
  if (!refreshedToken) throw new Error('No access token received');

  setToken(refreshedToken);
  return refreshedToken.accessToken;
};
